var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { createEvent } from '../core/EventEmitter';
import { collectFocusable } from '../utils/focusOrder';
var EventManager = /** @class */ (function () {
    function EventManager(app, element) {
        this.dragState = null;
        this.hoverNode = null;
        this.focusedNode = null;
        this.boundHandlers = {};
        this.app = app;
        this.element = element;
        if (!element.hasAttribute('tabindex')) {
            element.tabIndex = 0;
        }
        element.setAttribute('role', 'application');
        this.bindEvents();
    }
    EventManager.prototype.getFocusedNode = function () {
        return this.focusedNode;
    };
    EventManager.prototype.setFocus = function (node, originalEvent) {
        if (this.focusedNode === node)
            return;
        var prev = this.focusedNode;
        this.focusedNode = node;
        if (prev) {
            this.dispatchDirect(prev, 'blur', originalEvent !== null && originalEvent !== void 0 ? originalEvent : new Event('blur'), 0, 0, 0, 0);
        }
        if (node) {
            this.dispatchDirect(node, 'focus', originalEvent !== null && originalEvent !== void 0 ? originalEvent : new Event('focus'), 0, 0, 0, 0);
        }
        this.app.requestRender();
    };
    EventManager.prototype.bindEvents = function () {
        var e_1, _a;
        var _this = this;
        var events = [
            'click',
            'dblclick',
            'mousedown',
            'mouseup',
            'mousemove',
            'wheel',
            'touchstart',
            'touchmove',
            'touchend',
            'keydown',
            'keyup',
        ];
        var _loop_1 = function (type) {
            var handler = function (e) { return _this.handleEvent(type, e); };
            this_1.boundHandlers[type] = handler;
            // Wheel must be non-passive so chart zoom can call preventDefault (block page scroll).
            this_1.element.addEventListener(type, handler, {
                passive: type === 'touchmove',
            });
        };
        var this_1 = this;
        try {
            for (var events_1 = __values(events), events_1_1 = events_1.next(); !events_1_1.done; events_1_1 = events_1.next()) {
                var type = events_1_1.value;
                _loop_1(type);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (events_1_1 && !events_1_1.done && (_a = events_1.return)) _a.call(events_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    };
    EventManager.prototype.destroy = function () {
        for (var type in this.boundHandlers) {
            this.element.removeEventListener(type, this.boundHandlers[type]);
        }
    };
    EventManager.prototype.resolveDraggableNode = function (node) {
        var cur = node;
        while (cur) {
            if (cur.draggable)
                return cur;
            cur = cur.parent;
        }
        return null;
    };
    /** Map world-space pointer delta into the dragged node's parent local space. */
    EventManager.prototype.dragPositionFromWorld = function (node, startWorldX, startWorldY, worldX, worldY, nodeStartX, nodeStartY) {
        var parent = node.parent;
        if (!parent) {
            return {
                x: nodeStartX + (worldX - startWorldX),
                y: nodeStartY + (worldY - startWorldY),
            };
        }
        var inv = parent.getWorldMatrix().invert();
        if (!inv) {
            return {
                x: nodeStartX + (worldX - startWorldX),
                y: nodeStartY + (worldY - startWorldY),
            };
        }
        var p0 = inv.transformPoint(startWorldX, startWorldY);
        var p1 = inv.transformPoint(worldX, worldY);
        return { x: nodeStartX + (p1.x - p0.x), y: nodeStartY + (p1.y - p0.y) };
    };
    EventManager.prototype.getPointerCoords = function (e) {
        var rect = this.element.getBoundingClientRect();
        if ('touches' in e && e.touches.length > 0) {
            var touch = e.touches[0];
            return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
        }
        if ('clientX' in e) {
            return { x: e.clientX - rect.left, y: e.clientY - rect.top };
        }
        return { x: 0, y: 0 };
    };
    EventManager.prototype.handleEvent = function (type, originalEvent) {
        var _a, _b, _c, _d, _e, _f;
        if (type === 'keydown') {
            this.handleKeydown(originalEvent);
            return;
        }
        var _g = this.getPointerCoords(originalEvent), x = _g.x, y = _g.y;
        var world = this.app.camera.screenToWorld(x, y);
        if (this.dragState && (type === 'mousemove' || type === 'touchmove')) {
            var pos = this.dragPositionFromWorld(this.dragState.node, this.dragState.startX, this.dragState.startY, world.x, world.y, this.dragState.nodeStartX, this.dragState.nodeStartY);
            this.dragState.node.x = pos.x;
            this.dragState.node.y = pos.y;
            this.dragState.node.markDirty();
            this.dispatchBubble(this.dragState.node, 'dragmove', originalEvent, x, y, world.x, world.y, this.dragState.payload);
            var over = (_b = (_a = this.app.hitTest(world.x, world.y)) === null || _a === void 0 ? void 0 : _a.node) !== null && _b !== void 0 ? _b : null;
            if (over && over.dropTarget && over !== this.dragState.node) {
                this.dispatchBubble(over, 'dragover', originalEvent, x, y, world.x, world.y, this.dragState.payload);
                this.dragState.overNode = over;
            }
            else {
                this.dragState.overNode = null;
            }
            this.app.requestRender();
            return;
        }
        if (type === 'mouseup' || type === 'touchend') {
            if (this.dragState) {
                var dropTarget = this.dragState.overNode;
                this.dispatchBubble(this.dragState.node, 'dragend', originalEvent, x, y, world.x, world.y, this.dragState.payload);
                if (dropTarget && dropTarget !== this.dragState.node) {
                    this.dispatchBubble(dropTarget, 'drop', originalEvent, x, y, world.x, world.y, this.dragState.payload);
                }
                this.dragState = null;
            }
        }
        var hit = this.app.hitTest(world.x, world.y);
        var target = (_c = hit === null || hit === void 0 ? void 0 : hit.node) !== null && _c !== void 0 ? _c : null;
        if (type === 'mousemove' || type === 'touchmove') {
            if (target !== this.hoverNode) {
                if (this.hoverNode) {
                    this.dispatchBubble(this.hoverNode, 'mouseleave', originalEvent, x, y, world.x, world.y);
                }
                if (target) {
                    this.dispatchBubble(target, 'mouseenter', originalEvent, x, y, world.x, world.y);
                }
                this.hoverNode = target;
            }
        }
        if (target) {
            if (type === 'click' || type === 'dblclick') {
                if (target.focusable) {
                    this.setFocus(target, originalEvent);
                }
            }
            this.dispatchBubble(target, type, originalEvent, x, y, world.x, world.y);
            if (type === 'mousedown' || type === 'touchstart') {
                var dragNode = this.resolveDraggableNode(target);
                if (dragNode) {
                    this.dragState = {
                        node: dragNode,
                        startX: world.x,
                        startY: world.y,
                        nodeStartX: dragNode.x,
                        nodeStartY: dragNode.y,
                        payload: (_d = dragNode.dragPayload) !== null && _d !== void 0 ? _d : (_e = dragNode.metadata) === null || _e === void 0 ? void 0 : _e.dragPayload,
                        overNode: null,
                    };
                    this.dispatchBubble(dragNode, 'dragstart', originalEvent, x, y, world.x, world.y, this.dragState.payload);
                }
            }
        }
        if (type === 'wheel') {
            var wheelEvent = originalEvent;
            var hit_1 = this.app.hitTest(world.x, world.y);
            var target_1 = (_f = hit_1 === null || hit_1 === void 0 ? void 0 : hit_1.node) !== null && _f !== void 0 ? _f : null;
            var handled = false;
            if (target_1) {
                var event_1 = createEvent('wheel', target_1, originalEvent, x, y, world.x, world.y);
                var current = target_1;
                while (current) {
                    if (current.listening) {
                        event_1.currentTarget = current;
                        current.emit('wheel', event_1);
                        if (event_1.propagationStopped) {
                            handled = true;
                            break;
                        }
                    }
                    current = current.parent;
                }
            }
            if (!handled) {
                this.app.camera.pan(wheelEvent.deltaX / this.app.camera.zoom, wheelEvent.deltaY / this.app.camera.zoom);
            }
            else {
                wheelEvent.preventDefault();
            }
            return;
        }
    };
    EventManager.prototype.handleKeydown = function (e) {
        var focusable = collectFocusable(this.app.stage);
        if (e.key === 'Tab' && focusable.length > 0) {
            e.preventDefault();
            var idx = this.focusedNode ? focusable.indexOf(this.focusedNode) : -1;
            var delta = e.shiftKey ? -1 : 1;
            var next = focusable[(idx + delta + focusable.length) % focusable.length];
            this.setFocus(next, e);
            return;
        }
        if ((e.key === 'Enter' || e.key === ' ') && this.focusedNode) {
            e.preventDefault();
            this.dispatchBubble(this.focusedNode, 'click', e, 0, 0, 0, 0);
        }
    };
    /** Dispatch without bubbling (focus/blur). */
    EventManager.prototype.dispatchDirect = function (node, type, originalEvent, x, y, worldX, worldY, payload) {
        if (!node.listening)
            return;
        var event = createEvent(type, node, originalEvent, x, y, worldX, worldY, payload);
        event.currentTarget = node;
        node.emit(type, event);
        this.app.emit(type, __assign(__assign({}, event), { target: node }));
    };
    EventManager.prototype.dispatchBubble = function (node, type, originalEvent, x, y, worldX, worldY, payload) {
        var event = createEvent(type, node, originalEvent, x, y, worldX, worldY, payload);
        var current = node;
        while (current) {
            if (current.listening) {
                event.currentTarget = current;
                current.emit(type, event);
                if (event.propagationStopped)
                    break;
            }
            current = current.parent;
        }
        if (!event.propagationStopped) {
            this.app.emit(type, __assign(__assign({}, event), { target: node }));
        }
    };
    return EventManager;
}());
export { EventManager };
